import { useNavigate } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { Progress } from "@/components/ui/progress"
import { Upload, ArrowLeft, Play, Pause, RotateCcw, X } from "lucide-react"
import { useMultiFileUpload } from "@/lib/hooks/useFileUpload"
import { useBatchUpload } from "@/lib/hooks/useBatchUpload"
import { FileQueueTable } from "@/components/FileQueueTable"
import { toast } from "sonner"
import { useEffect } from "react"

export default function BatchUploadPage() {
  const navigate = useNavigate()
  const { files, isDragging, handleFileDrop, handleFileSelect, handleDragOver, handleDragLeave } = useMultiFileUpload()
  const {
    filesWithMetadata,
    isProcessing,
    isPaused,
    overallProgress,
    addFiles,
    updateFileMetadata,
    removeFile,
    clearAll,
    uploadFiles,
    pauseUpload,
    resumeUpload,
    retryFailed,
    getStats,
  } = useBatchUpload()

  // Add files to batch processor when they're selected
  useEffect(() => {
    if (files.length > 0) {
      addFiles(files)
    }
  }, [files, addFiles])

  const stats = getStats()
  const canStart = stats.total > 0 && !isProcessing && (stats.pending > 0 || stats.error > 0)
  const hasErrors = stats.error > 0

  const handleStartUpload = async () => {
    // Validate that all files have required metadata
    const invalidFiles = filesWithMetadata.filter(
      f => (f.status === "pending" || f.status === "error") && (!f.metadata.title || !f.metadata.year || !f.metadata.region || !f.metadata.subject)
    )

    if (invalidFiles.length > 0) {
      toast.error(`${invalidFiles.length} 个文件缺少必填信息，请补充完整`)
      return
    }

    try {
      await uploadFiles()
      toast.success("批量上传完成！")
    } catch (error) {
      toast.error("批量上传失败")
      console.error(error)
    }
  }

  const handleBack = () => {
    if (isProcessing) {
      const confirm = window.confirm("上传正在进行中，确定要离开吗？")
      if (!confirm) return
    }
    navigate("/admin")
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="mx-auto flex h-14 max-w-7xl items-center justify-between px-4 sm:h-16 sm:px-6">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={handleBack}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <h1 className="text-lg font-semibold text-foreground sm:text-xl">批量上传试卷</h1>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 sm:py-8">
        <div className="space-y-6">
          {/* File Upload Area */}
          {filesWithMetadata.length === 0 && (
            <Card>
              <CardContent className="p-6">
                <div
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`relative flex min-h-[300px] cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-border bg-muted/10 hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  <div className="flex flex-col items-center gap-4 text-center">
                    <div className="rounded-full bg-primary/10 p-6">
                      <Upload className="h-12 w-12 text-primary" />
                    </div>
                    <div>
                      <p className="mb-2 text-lg font-medium text-foreground">拖拽文件或文件夹到这里</p>
                      <p className="text-sm text-muted-foreground">或点击选择多个 PDF 文件</p>
                      <p className="mt-2 text-xs text-muted-foreground">支持一次上传大量文件，系统会自动解析文件名</p>
                    </div>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={handleFileSelect}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </div>
              </CardContent>
            </Card>
          )}

          {/* Statistics */}
          {filesWithMetadata.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h2 className="text-lg font-semibold">上传进度</h2>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={clearAll}
                        disabled={isProcessing}
                      >
                        <X className="mr-2 h-4 w-4" />
                        清空列表
                      </Button>
                      {hasErrors && !isProcessing && (
                        <Button variant="outline" size="sm" onClick={retryFailed}>
                          <RotateCcw className="mr-2 h-4 w-4" />
                          重试失败
                        </Button>
                      )}
                      {isPaused ? (
                        <Button size="sm" onClick={resumeUpload}>
                          <Play className="mr-2 h-4 w-4" />
                          继续上传
                        </Button>
                      ) : isProcessing ? (
                        <Button size="sm" variant="outline" onClick={pauseUpload}>
                          <Pause className="mr-2 h-4 w-4" />
                          暂停
                        </Button>
                      ) : (
                        <Button size="sm" onClick={handleStartUpload} disabled={!canStart}>
                          <Play className="mr-2 h-4 w-4" />
                          开始上传
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="grid grid-cols-5 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold">{stats.total}</div>
                      <div className="text-sm text-muted-foreground">总计</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-muted-foreground">{stats.pending}</div>
                      <div className="text-sm text-muted-foreground">待上传</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-blue-500">{stats.uploading}</div>
                      <div className="text-sm text-muted-foreground">上传中</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-green-500">{stats.success}</div>
                      <div className="text-sm text-muted-foreground">成功</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-red-500">{stats.error}</div>
                      <div className="text-sm text-muted-foreground">失败</div>
                    </div>
                  </div>

                  {isProcessing && (
                    <div className="space-y-2">
                      <Progress value={overallProgress} />
                      <p className="text-center text-sm text-muted-foreground">{overallProgress}% 完成</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          )}

          {/* File Queue Table */}
          {filesWithMetadata.length > 0 && (
            <Card>
              <CardContent className="p-6">
                <FileQueueTable
                  files={filesWithMetadata}
                  onUpdateMetadata={updateFileMetadata}
                  onRemoveFile={removeFile}
                />
              </CardContent>
            </Card>
          )}

          {/* Add More Files Button */}
          {filesWithMetadata.length > 0 && !isProcessing && (
            <Card>
              <CardContent className="p-4">
                <div
                  onDrop={handleFileDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  className={`relative flex min-h-[100px] cursor-pointer items-center justify-center rounded-lg border-2 border-dashed transition-colors ${
                    isDragging
                      ? "border-primary bg-primary/5"
                      : "border-border bg-muted/10 hover:border-primary hover:bg-primary/5"
                  }`}
                >
                  <div className="text-center">
                    <Upload className="mx-auto mb-2 h-6 w-6 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">添加更多文件</p>
                  </div>
                  <input
                    type="file"
                    accept=".pdf"
                    multiple
                    onChange={handleFileSelect}
                    className="absolute inset-0 cursor-pointer opacity-0"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  )
}
